/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import React, { lazy, Suspense } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { useGetInTrialFeatureGateBannerData } from '../../services/use-get-user-tenant-experience-data';

import type { InTrialFeatureGatesBannerWithDataProps } from './types';

const InTrialFeatureGatesBanner = lazy(() => import('../in-trial-feature-gates-banner'));

/**
 * Creating a higher order component to wrap the InTrialFeatureGatesBanner to make Storybook and testing simpler.
 * @returns {JSX.Element} - InTrialFeatureGatesBanner component
 */
export const InTrialFeatureGatesBannerWithData = ({
	featureId,
	// AGG client to refetch pre-fetched data from the GQL cache
	tenantId,
	edition,
	sendOperationalEvent,
	sendScreenEvent,
	sendTrackEvent,
	...props
}: InTrialFeatureGatesBannerWithDataProps) => {
	const { daysLeftInTrial, paymentFlowExperienceUrl, error, loading } =
		useGetInTrialFeatureGateBannerData({
			sendOperationalEvent,
			tenantId,
			featureId,
			...props,
		});

	// In order for us to even be able to render this experience, we will already have verified that the user is in a valid audience. Adding the payment details and trial check here is just a typecheck for below.
	if (error || loading || !daysLeftInTrial || !paymentFlowExperienceUrl) {
		return null;
	}

	return (
		<ErrorBoundary
			fallback={<div />}
			onError={(error) => {
				sendOperationalEvent({
					type: 'sendOperationalEvent',
					action: 'error',
					actionSubject: 'luna-growth-experiment-error-boundary',
					actionSubjectId: 'in-trial-feature-gates-banner-error-boundary',
					attributes: {
						error: error.message,
						tenantId,
						featureId,
					},
					source: 'in_trial_feature_gates_banner',
				});
			}}
		>
			<Suspense fallback={<div />}>
				<InTrialFeatureGatesBanner
					daysLeftInTrial={daysLeftInTrial}
					paymentFlowExperienceUrl={paymentFlowExperienceUrl}
					featureId={featureId}
					sendScreenEvent={sendScreenEvent}
					sendTrackEvent={sendTrackEvent}
					edition={edition}
				/>
			</Suspense>
		</ErrorBoundary>
	);
};
